import { useState, useEffect, useCallback } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import { formatBreakupAmount } from "../utils/formatBreakupAmounts";
import { formatIsoDateRangeOrdinalMonth } from "../utils/formatIsoDateOrdinalMonth";
import type { ExpenseThisMonthCardProps } from "../components/expense_components/ExpenseThisMonthCard";
import {
  getExpenseBootstrapData,
  getExpenseBootstrapError,
  getExpenseBootstrapPromise,
} from "../state/expenseBootstrapStore";
import { useExpenseBootstrap } from "./useExpenseBootstrap";

export function useExpenseThisMonth(): ExpenseThisMonthCardProps & {
  amountThisWeek: string;
  amountThisMonth: string;
  subTotalThisWeek: string;
  taxTotalThisWeek: string;
  subTotalThisMonth: string;
  taxTotalThisMonth: string;
  weekBadgeLabel: string;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useExpenseBootstrap();

  const [card, setCard] = useState<ExpenseThisMonthCardProps>({
    title: "BILLED THIS MONTH",
    subtext: "—",
    amount: "—",
    amountLabel: "Est. Bills",
    badgePrimary: "This Month",
    badgeSecondary: "—",
    showEst: true,
    loading: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [amountThisWeek, setAmountThisWeek] = useState("—");
  const [amountThisMonth, setAmountThisMonth] = useState("—");
  const [subTotalThisWeek, setSubTotalThisWeek] = useState("—");
  const [taxTotalThisWeek, setTaxTotalThisWeek] = useState("—");
  const [subTotalThisMonth, setSubTotalThisMonth] = useState("—");
  const [taxTotalThisMonth, setTaxTotalThisMonth] = useState("—");
  const [weekBadgeLabel, setWeekBadgeLabel] = useState("—");

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getExpenseBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getExpenseBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getExpenseBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Expense bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.expenseThisMonth;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Expense this month unavailable");
      }

      const entity = section.data;
      const monthLine = entity.monthLabel?.trim() || "Month unavailable";
      const rangeLine = formatIsoDateRangeOrdinalMonth(
        entity.weekStart,
        entity.weekEnd,
      );
      const periodLabel = rangeLine ?? "Week dates unavailable";
      const subtext = `${monthLine} · ${periodLabel}`;
      const badgeSecondary = `Week ${entity.weekNumberInMonth} of ${entity.totalWeeksInMonth}`;
      // Headline totals: keep API totals and existing formatting.
      const weekAmount = formatPlAmount(entity.expenseThisWeek);
      const monthAmount = formatPlAmount(entity.expenseThisMonth);

      // Breakups: use round-to-decimals formatter (different from formatPlAmount truncation).
      const weekSubTotal = formatBreakupAmount(entity.subTotalThisWeek);
      const weekTaxTotal = formatBreakupAmount(entity.taxTotalThisWeek);
      const monthSubTotal = formatBreakupAmount(entity.subTotalThisMonth);
      const monthTaxTotal = formatBreakupAmount(entity.taxTotalThisMonth);
      setAmountThisWeek(weekAmount);
      setAmountThisMonth(monthAmount);
      setSubTotalThisWeek(weekSubTotal);
      setTaxTotalThisWeek(weekTaxTotal);
      setSubTotalThisMonth(monthSubTotal);
      setTaxTotalThisMonth(monthTaxTotal);
      setWeekBadgeLabel(badgeSecondary);

      setCard({
        title: "BILLED THIS MONTH",
        subtext,
        amount: monthAmount,
        amountLabel: "Paid this week",
        badgePrimary: "This Month",
        badgeSecondary,
        showEst: false,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load expense this week";
      setError(message);
      setCard((prev) => ({
        ...prev,
        subtext: "—",
        amount: "—",
        badgeSecondary: "—",
      }));
      setAmountThisWeek("—");
      setAmountThisMonth("—");
      setSubTotalThisWeek("—");
      setTaxTotalThisWeek("—");
      setSubTotalThisMonth("—");
      setTaxTotalThisMonth("—");
      setWeekBadgeLabel("—");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    ...card,
    amountThisWeek,
    amountThisMonth,
    subTotalThisWeek,
    taxTotalThisWeek,
    subTotalThisMonth,
    taxTotalThisMonth,
    weekBadgeLabel,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
